import React, { Suspense } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { Spinner } from "@cinesys/ui/components";
import CustomerLayout from "./components/CustomerLayout";
import NotFoundPage from "./components/NotFoundPage";
import DelayedSpinner from "./components/DelayedSpinner";
import InterfaceAccessNotice from "./components/InterfaceAccessNotice";
import { useInterfaceAccess } from "./hooks/useInterfaceAccess";
import VaultThemeProvider from "./theme/VaultThemeProvider";
import AbScreenShell from "./features/ab-vault/AbScreenShell";
import VaultToolGate from "./features/ab-vault/VaultToolGate";
import type { VaultTool } from "./features/ab-vault/config";
import SpotterThemeProvider from "./theme/SpotterThemeProvider";
import MetadataImportThemeProvider from "./theme/MetadataImportThemeProvider";
import SpotterTabLoadingShell from "./features/spotter/SpotterTabLoadingShell";
import StartFailureHost from "./features/spotter/filmsGround/StartFailureHost";
import SpotterRemoteRoute, {
  spotterRemoteEnabled,
} from "./features/remote-app/SpotterRemoteRoute";

// DEV-ONLY launcher home (token input + links to each interface route). Never
// reachable in a prod build — mirrors vault-frontend's AppRouter IS_DEV gate
// (apps/vault-frontend/src/AppRouter.tsx), adapted to this app's index route.
const IS_DEV = process.env.NODE_ENV !== "production";
const DevHome = React.lazy(() => import("./features/dev/DevHome"));

// Lazy-load feature screens — each customer org is provisioned exactly ONE
// interface, so these should never all land in the same chunk (mirrors
// vault-frontend's AppRouter lazy-loading pattern).
const CandidateAssignmentsScreen = React.lazy(
  () =>
    import(
      "./features/ab-vault/candidate-assignments/CandidateAssignmentsScreen"
    )
);
const PlaceholdersScreen = React.lazy(
  () => import("./features/ab-vault/placeholders/PlaceholdersScreen")
);
const UploadScreen = React.lazy(
  () => import("./features/ab-vault/upload/UploadScreen")
);
const FieldsScreen = React.lazy(
  () => import("./features/spotter/fields/FieldsScreen")
);
const FrScreen = React.lazy(
  () => import("./features/spotter/facialRecognition/FrScreen")
);
const GamebookScreen = React.lazy(
  () => import("./features/spotter/gamebook/GamebookScreen")
);
const RenameScreen = React.lazy(
  () => import("./features/spotter/rename/RenameScreen")
);
const UsageScreen = React.lazy(
  () => import("./features/spotter/usage/UsageScreen")
);
const ControlRoomScreen = React.lazy(
  () => import("./features/spotter/controlRoom/ControlRoomScreen")
);

/** Redirects a `:jobId` route param through unchanged — used below so a `spotter/anchor-review/
 *  :jobId` resume-url already in the wild still lands on the right job's review panel, just
 *  inside the consolidated Control Room page now. Not lazy (trivial, no chunk worth splitting
 *  out) — reading `useParams` here (rather than a static string) is what keeps the redirect
 *  job-scoped instead of always bouncing to the bare list. */
const AnchorReviewToControlRoomRedirect = () => {
  const { jobId } = useParams<{ jobId: string }>();
  return (
    <Navigate
      to={jobId ? `/spotter/control-room/${jobId}` : "/spotter/control-room"}
      replace
    />
  );
};

/** Each Spotter tab gets its own Suspense so a tab's chunk loading keeps the Spotter header on
 *  screen; the theme provider sits outside it so the fallback has the `--spotter*` vars. */
const SpotterTab = ({ children }: { children: React.ReactNode }) => (
  <SpotterThemeProvider>
    <Suspense fallback={<SpotterTabLoadingShell />}>{children}</Suspense>
  </SpotterThemeProvider>
);

/** The in-tree Spotter, relative to `/spotter`. Both Control Room paths share one component so
 *  the screen stays mounted between them; old Anchor Review links redirect into it. */
const SPOTTER_IN_TREE_ROUTES: { path: string; element: React.ReactNode }[] = [
  {
    path: "fields",
    element: (
      <SpotterTab>
        <FieldsScreen />
      </SpotterTab>
    ),
  },
  {
    path: "facial-recognition",
    element: (
      <SpotterTab>
        <FrScreen />
      </SpotterTab>
    ),
  },
  {
    path: "gamebook",
    element: (
      <SpotterTab>
        <GamebookScreen />
      </SpotterTab>
    ),
  },
  {
    path: "rename",
    element: (
      <SpotterTab>
        <RenameScreen />
      </SpotterTab>
    ),
  },
  {
    path: "usage",
    element: (
      <SpotterTab>
        <UsageScreen />
      </SpotterTab>
    ),
  },
  { path: "anchor-review", element: <AnchorReviewToControlRoomRedirect /> },
  {
    path: "anchor-review/:jobId",
    element: <AnchorReviewToControlRoomRedirect />,
  },
  {
    path: "control-room",
    element: (
      <SpotterTab>
        <ControlRoomScreen />
      </SpotterTab>
    ),
  },
  {
    path: "control-room/:jobId",
    element: (
      <SpotterTab>
        <ControlRoomScreen />
      </SpotterTab>
    ),
  },
];

/** The in-tree Spotter under a `spotter/*` route, used when the remote bundle can't load.
 *  Unknown paths go to Fields, as they do in the remote. */
const InTreeSpotterRoutes = () => (
  <Routes>
    {SPOTTER_IN_TREE_ROUTES.map(({ path, element }) => (
      <Route key={path} path={path} element={element} />
    ))}
    <Route path="*" element={<Navigate to="/spotter/fields" replace />} />
  </Routes>
);

// M3+ interface: Metadata Import & Enrichment.
const MetadataImportOverview = React.lazy(
  () => import("./features/metadata-import/OverviewScreen")
);
const MetadataImportProfile = React.lazy(
  () => import("./features/metadata-import/ProfileScreen")
);

/**
 * REAL PRODUCTION UI, not a placeholder to delete: per spec §2.3, a customer
 * who lands with a valid token but whose account has no interface enabled
 * sees exactly this screen. Only rendered in production (or in dev when
 * `IS_DEV`'s `DevHome` isn't in play) — see the `index` route below.
 */
const InterfaceHome = () => (
  <div style={{ padding: 40, color: "var(--dark700)" }}>
    No interfaces enabled for this account yet.
  </div>
);

/**
 * Auth-aware wrapper around `InterfaceHome`, mounted at the index route in production. Bare `/`
 * has no specific interface to check (there's nothing here to grant access TO), so it calls
 * `useInterfaceAccess(null)` — same shared `customer-context` query every interface screen
 * already uses, no second auth mechanism. A truly unauthenticated visitor (no/expired session)
 * now gets `InterfaceAccessNotice`'s real "sign in at iconik" copy instead of the context-free
 * `InterfaceHome` message; every other outcome (pending, or authenticated with or without an
 * enabled interface) renders `InterfaceHome` exactly as before this change.
 */
const IndexRoute = () => {
  const access = useInterfaceAccess(null);
  if (access.status === "pending") {
    return <DelayedSpinner padTop />;
  }
  if (access.status === "denied" && access.reason === "unauthenticated") {
    return <InterfaceAccessNotice reason="unauthenticated" />;
  }
  return <InterfaceHome />;
};

/** Keeps the Vault header and nav on screen while a lazily loaded screen's chunk downloads, and
 *  only mounts the screen for a user who may use `tool`. */
const VaultRoute = ({
  tool,
  children,
}: {
  tool: VaultTool;
  children: React.ReactNode;
}) => (
  <VaultThemeProvider>
    <VaultToolGate tool={tool}>
      <Suspense fallback={<AbScreenShell loading>{null}</AbScreenShell>}>
        {children}
      </Suspense>
    </VaultToolGate>
  </VaultThemeProvider>
);

/**
 * Root SPA router. `basename="/"` — the customer SPA sits at the domain root,
 * unlike vault's `basename="/ui"` (spec §2.2/Appendix C Conflict 3).
 *
 * No session gate here: `index.tsx` mounts the app unconditionally (with or without a URL
 * token) and `CustomerApiService` gates authenticated requests on the boot exchange instead
 * (see its own doc comment). There used to be a reactive full-page swap here too (any 401
 * anywhere dispatched `SESSION_EXPIRED_EVENT`, which flipped local state and rendered
 * `<ExpiredTokenPage>` in place of the whole route tree) for a session that died mid-use, kept
 * deliberately separate from an unauthenticated visitor's initial 401 (handled locally, per
 * interface — see `useInterfaceAccess`). Addison: those are the same situation from the user's
 * side, so both are now handled the SAME way, entirely inside `useInterfaceAccess` — nothing left
 * for this router to gate on, so the listener/swap and `<ExpiredTokenPage>` itself are gone.
 * The index route (`IndexRoute`, above) checks the SAME way, locally, via `useInterfaceAccess(null)`
 * — it just has no specific interface to check, so it only distinguishes "unauthenticated" from
 * everything else rather than gating on a particular `enabledInterfaces` entry.
 *
 * `CustomerLayout` is mounted as a layout route (renders its children via
 * `<Outlet />`) wrapping every route except the catch-all 404. It is
 * intentionally header-less/nav-less (see its own doc comment) — every
 * interface route below opts into its OWN route-scoped theme + header: `ab-
 * vault/candidate-assignments` wraps in `VaultThemeProvider` (the `--ab*` teal
 * palette), and every `spotter/*` route wraps in `SpotterThemeProvider` (the
 * `--spotter*` slate/amber palette) plus renders `SpotterHeader` itself. One
 * customer org is ever provisioned exactly ONE of these interfaces, so there
 * is deliberately no cross-linking UI between them here or anywhere else in
 * the app.
 *
 * Catch-all 404 (`path="*"`, below): any path that doesn't match one of the
 * interface routes renders `NotFoundPage` full-page. This used to fall
 * through to the nested `CustomerLayout` catch-all, which rendered
 * `InterfaceHome` ("No interfaces enabled for this account yet.") for
 * typo'd/stale/unknown paths too — the wrong message for "this route doesn't
 * exist" vs. its real meaning ("your account has no interface provisioned"),
 * which only applies at the exact index route.
 */
const AppRouter = () => {
  return (
    <BrowserRouter basename="/">
      <Suspense fallback={<Spinner />}>
        <Routes>
          <Route element={<CustomerLayout />}>
            <Route index element={IS_DEV ? <DevHome /> : <IndexRoute />} />
            <Route
              path="ab-vault/candidate-assignments"
              element={
                <VaultRoute tool="candidates">
                  <CandidateAssignmentsScreen />
                </VaultRoute>
              }
            />
            <Route
              path="ab-vault/placeholders"
              element={
                <VaultRoute tool="placeholder">
                  <PlaceholdersScreen />
                </VaultRoute>
              }
            />
            <Route
              path="ab-vault/create-placeholder"
              element={<Navigate to="/ab-vault/placeholders" replace />}
            />
            <Route
              path="ab-vault/bulk-placeholder"
              element={
                <Navigate to="/ab-vault/placeholders?mode=bulk" replace />
              }
            />
            <Route
              path="ab-vault/upload"
              element={
                <VaultRoute tool="upload">
                  <UploadScreen />
                </VaultRoute>
              }
            />
            <Route
              path="ab-vault/single-upload"
              element={<Navigate to="/ab-vault/upload" replace />}
            />
            <Route
              path="ab-vault/bulk-upload"
              element={<Navigate to="/ab-vault/upload" replace />}
            />
            <Route
              path="ab-vault/my-placeholders"
              element={<Navigate to="/ab-vault/placeholders" replace />}
            />
            {spotterRemoteEnabled() ? (
              <Route
                path="spotter/*"
                element={
                  <SpotterRemoteRoute fallback={<InTreeSpotterRoutes />} />
                }
              />
            ) : (
              SPOTTER_IN_TREE_ROUTES.map(({ path, element }) => (
                <Route key={path} path={`spotter/${path}`} element={element} />
              ))
            )}
            {/* Metadata Import & Enrichment — a reusable Customer Portal interface, sibling of
                AB Vault / Spotter. Its own MetadataImportThemeProvider (`--mi*` palette) wrapping
                a per-route Suspense. Overview = the profiles list; `:profileId` = the profile
                detail (six in-page tabs). */}
            <Route
              path="metadata-import"
              element={
                <MetadataImportThemeProvider>
                  <Suspense fallback={<Spinner />}>
                    <MetadataImportOverview />
                  </Suspense>
                </MetadataImportThemeProvider>
              }
            />
            <Route
              path="metadata-import/:profileId"
              element={
                <MetadataImportThemeProvider>
                  <Suspense fallback={<Spinner />}>
                    <MetadataImportProfile />
                  </Suspense>
                </MetadataImportThemeProvider>
              }
            />
            {/* M3+: additional interface routes go here, nested under CustomerLayout. */}
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <StartFailureHost />
    </BrowserRouter>
  );
};

export default AppRouter;
